import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../../lib/result-codes";
import type {
  ResidentReportBatchLoader,
  ResidentReportModel,
} from "../types/resident-report.types";

export function useResidentReportReader(
  loadBatch: ResidentReportBatchLoader,
  resetKey: string,
  pageSize = 25,
) {
  const [reports, setReports] = useState<ResidentReportModel[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { run, cancel, loading } = useLatestRequest();

  const request = useCallback(
    async (cursor: string | null, replace: boolean) => {
      setError(null);
      try {
        const result = await run((signal) => loadBatch({ cursor, pageSize, signal }));
        if (!result) return;
        setReports((current) => (replace ? result.items : [...current, ...result.items]));
        setNextCursor(result.nextCursor);
        setHasMore(result.nextCursor !== null);
      } catch (requestError: unknown) {
        setError(getApiErrorMessage(requestError, "Failed to load resident reports"));
      }
    },
    [loadBatch, pageSize, run],
  );

  const reset = useCallback(() => {
    cancel();
    setReports([]);
    setNextCursor(null);
    setHasMore(true);
    void request(null, true);
  }, [cancel, request]);

  useEffect(() => {
    reset();
  }, [reset, resetKey]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore || nextCursor === null) return;
    void request(nextCursor, false);
  }, [hasMore, loading, nextCursor, request]);

  return { reports, hasMore, error, loading, loadMore, reset };
}
